import { EventMeta } from '../../../interfaces/meta.interface';

type EnableMailboxDescFields = {
    archive: boolean;
    user: string;
};

// Enable-Mailbox operation
const enableMailbox: EventMeta<any, EnableMailboxDescFields> = {
    title: 'Mailbox for existing user Created',
    default: {
        description: 'Mailbox for existing user Created',
        longDescription: 'A mailbox was created for the existing user.',
        severity: 'info',
    },
    descriptionExtractor: (log: any) => {
        if (!log.Parameters) return { archive: undefined, user: undefined };
        const archive = log.Parameters.find((p: any) => p.Name === 'Archive')?.Value;
        const user = log.Parameters.find((p: any) => p.Name === 'Identity')?.Value;
        return { archive, user };
    },
    classifications: [
        // enable mailbox. fraudulent and not in allowed list.
        {
            severity: 'critical',
            description: (fields) =>
                fields.archive
                    ? `An archive mailbox was created for ${fields.user} from a suspicious IP`
                    : `A mailbox was created for ${fields.user} from a suspicious IP`,
            longDescription: (fields) =>
                fields.archive
                    ? `An archive mailbox was created for the existing user ${fields.user}. The action originated from a suspicious IP that is not on your allowed list. Because archive creation can alter retention and visibility, investigate promptly.`
                    : `A mailbox was created for the existing user ${fields.user}. The action originated from a suspicious IP that is not on your allowed list. Treat this as potentially malicious and review the actor and intent immediately.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isFraudulentIP: true,
            },
        },
        // enable mailbox. not in allowed list.
        {
            severity: 'danger',
            description: (fields) =>
                fields.archive
                    ? `An archive mailbox was created for ${fields.user} from an unapproved IP`
                    : `A mailbox was created for ${fields.user} from a unapproved IP`,
            longDescription: (fields) =>
                fields.archive
                    ? `An archive mailbox was created for the existing user ${fields.user}. The action originated from an unapproved IP that is not on your allowed list. Because archive creation can alter retention and visibility, investigate promptly.`
                    : `A mailbox was created for the existing user ${fields.user}. The action originated from an unapproved IP that is not on your allowed list. Treat this as potentially malicious and review the actor and intent immediately.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
            },
        },
        // enable mailbox. fraudulent IP.
        {
            severity: 'warning',
            description: (fields) =>
                fields.archive
                    ? `An archive mailbox was created for ${fields.user} from an suspicious IP`
                    : `A mailbox was created for ${fields.user} from a suspicious IP`,
            longDescription: (fields) =>
                fields.archive
                    ? `An archive mailbox was created for the existing user ${fields.user}. The action originated from a suspicious IP that is within your allowed list. Because archive creation can alter retention and visibility, investigate promptly.`
                    : `A mailbox was created for the existing user ${fields.user}. The action originated from a suspicious IP that is within your allowed list. Treat this as potentially malicious and review the actor and intent immediately.`,
            predicates: {
                isFraudulentIP: true,
            },
        },
        // enable mailbox.
        {
            severity: 'info',
            description: (fields) =>
                fields.archive
                    ? `An archive mailbox was created for ${fields.user}`
                    : `A mailbox was created for ${fields.user}`,
            longDescription: (fields) =>
                fields.archive
                    ? `An archive mailbox was created for the existing user ${fields.user}. This event is informational — it does not require immediate action.`
                    : `A mailbox was created for the existing user ${fields.user}. This event is informational — it does not require immediate action.`,
        },
    ],
};

export default enableMailbox;
